import PouchDB from "pouchdb";
import { useCallback, useState } from "react";
import { useDatabases } from "./useDatabases";
import { DatabaseIdentifier } from "./manager";
import { ProjectInventory } from "./types/projects";
import { useCurrentProject } from "./useCurrentProject";
import { useHandlePouchChanges } from "./useHandlePouchChanges";

interface PouchUpdateReturn<T> {
  execute: (doc: PouchDB.Core.PutDocument<T>) => Promise<T & PouchDB.Core.IdMeta & PouchDB.Core.RevisionIdMeta>;
  updating: boolean;
  error: unknown | null;
}

export function usePouchUpdate<T>(database: PouchDB.Database<T> | undefined, dependencies: DatabaseIdentifier[]): PouchUpdateReturn<T> {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<unknown | null>(null);

  const handlePouchChanges = useHandlePouchChanges();

  const execute = useCallback(
    async (doc: PouchDB.Core.PutDocument<T>) => {
      if (!database) {
        throw new Error("Database not available");
      }
      try {
        setUpdating(true);
        setError(null);
        console.log("[POUCH UPDATE]", database.name, doc);
        const resp = await database.put(doc);
        handlePouchChanges(dependencies).then(() => {});
        return { ...doc, _id: resp.id, _rev: resp.rev };
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setUpdating(false);
      }
    },
    [database, dependencies, handlePouchChanges]
  );

  return { updating, error, execute };
}

export function useUpdateProject() {
  const { projects } = useDatabases();
  return usePouchUpdate(projects, ["projects"]);
}

export function useUpdateSetting() {
  const { settings } = useDatabases();
  return usePouchUpdate(settings, ["settings"]);
}

export function useUpdateFormTemplate() {
  const { formTemplates } = useDatabases();
  return usePouchUpdate(formTemplates, ["formTemplates"]);
}

export function useUpdateInventory() {
  const { data: project } = useCurrentProject();
  const { execute: updateProject, updating, error } = useUpdateProject();

  const execute = useCallback(
    async (inventoryId: string, data: ProjectInventory) => {
      if (!project) {
        throw new Error("Cannot update non-existent project");
      }
      return await updateProject({
        ...project,
        inventories: {
          ...project.inventories,
          [inventoryId]: data,
        },
      });
    },
    [project, updateProject]
  );

  return { execute, updating, error };
}
